import { ExclamationTriangleIcon, ReloadIcon } from "@radix-ui/react-icons";
import { Button, Card, Code, Flex, Heading, Text } from "@radix-ui/themes";
import * as Sentry from "@sentry/react";
import type React from "react";

interface ErrorBoundaryFallbackProps {
    error: Error;
    componentStack: string | null;
    resetError: () => void;
}

/**
 * Fallback UI component shown when an error is caught by the error boundary
 */
function ErrorBoundaryFallback({ error, componentStack, resetError }: ErrorBoundaryFallbackProps) {
    return (
        <Flex
            direction="column"
            align="center"
            justify="center"
            style={{
                minHeight: "100vh",
                padding: "2rem",
                background: "var(--gray-2)",
            }}
        >
            <Card
                size="4"
                style={{
                    maxWidth: "600px",
                    width: "100%",
                }}
            >
                <Flex direction="column" gap="4" align="center">
                    <Flex align="center" gap="2">
                        <ExclamationTriangleIcon width="32" height="32" color="red" />
                        <Heading size="6">Something went wrong</Heading>
                    </Flex>

                    <Text size="3" color="gray" align="center">
                        We're sorry, but something unexpected happened. The error has been reported to our team.
                    </Text>

                    {import.meta.env.MODE === "development" && (
                        <Card
                            style={{
                                width: "100%",
                                background: "var(--gray-3)",
                            }}
                        >
                            <Flex direction="column" gap="2">
                                <Text size="2" weight="bold">
                                    Error Details (Development Only):
                                </Text>
                                <Code
                                    size="2"
                                    style={{
                                        display: "block",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {error.message}
                                </Code>
                                {componentStack && (
                                    <details>
                                        <summary
                                            style={{
                                                cursor: "pointer",
                                                marginTop: "0.5rem",
                                            }}
                                        >
                                            <Text size="2">Component Stack</Text>
                                        </summary>
                                        <Code
                                            size="1"
                                            style={{
                                                display: "block",
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                                marginTop: "0.5rem",
                                                maxHeight: "200px",
                                                overflow: "auto",
                                            }}
                                        >
                                            {componentStack}
                                        </Code>
                                    </details>
                                )}
                            </Flex>
                        </Card>
                    )}

                    <Flex
                        gap="3"
                        style={{
                            marginTop: "1rem",
                        }}
                    >
                        <Button onClick={resetError} variant="soft" size="3">
                            <ReloadIcon />
                            Try Again
                        </Button>
                        {/* <Button onClick={() => (window.location.href = '/')} variant="outline" size="3">
              Go to Home
            </Button> */}
                    </Flex>
                </Flex>
            </Card>
        </Flex>
    );
}

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

/**
 * Error Boundary component using Sentry's ErrorBoundary
 * Wraps the entire application to catch and report React component errors
 */
export function ErrorBoundary({ children }: ErrorBoundaryProps) {
    return (
        <Sentry.ErrorBoundary
            fallback={({ error, componentStack, resetError }) => (
                <ErrorBoundaryFallback error={error} componentStack={componentStack} resetError={resetError} />
            )}
            beforeCapture={(scope) => {
                scope.setTag("error_boundary", "app_root");
            }}
        >
            {children}
        </Sentry.ErrorBoundary>
    );
}
